import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { normalizeImageUrl } from "@/lib/image-url";
import { apiRequest } from "@/lib/queryClient";
import imageCompression from "browser-image-compression";
import { Camera, ImageIcon, Loader2, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";

// 배열 비교 유틸리티 함수
function arraysEqual(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  return a.every((val, index) => val === b[index]);
}

interface UploadedImage {
  id: string;
  url: string;
  file?: File;
  uploading?: boolean;
  error?: string;
}

interface MarketImageUploaderProps {
  maxImages?: number;
  onImagesChange: (imagePaths: string[]) => void;
  userId?: string;
  initialImages?: string[];
  images?: string[];
}

/**
 * 뉴스 style 이미지 업로더
 *
 * Features:
 * - 최대 10장 업로드 (기본값)
 * - 드래그&드롭, 파일 선택, 모바일 카메라 촬영 지원
 * - 클라이언트 측 자동 이미지 압축 (핸드폰 사진 최적화)
 *   - 목표 크기: 1MB
 *   - 최대 해상도: 1920px (고해상도 디스플레이 지원)
 *   - 원본이 1MB 이하면 압축 생략
 * - 즉시 미리보기
 * - 백그라운드 업로드 (다른 폼 입력 가능)
 * - 이미지 순서 변경 (드래그)
 * - 개별 삭제
 */
export function MarketImageUploader({
  maxImages = 10,
  onImagesChange,
  userId,
  initialImages,
  images: controlledImages,
}: MarketImageUploaderProps) {
  const [images, setImages] = useState<UploadedImage[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const prevInitialImagesRef = useRef<string[]>([]);
  const isInitialMountRef = useRef(true);
  const { toast } = useToast();

  // Load initial images (for edit mode)
  // initialImages가 실제로 변경되었을 때만 업데이트 (무한 루프 방지)
  // 기존 이미지와 병합하여 사용자가 추가한 이미지가 사라지지 않도록 처리
  useEffect(() => {
    const currentInitialImages = initialImages || controlledImages || [];
    const prevInitialImages = prevInitialImagesRef.current;

    // 배열 내용이 동일하면 업데이트하지 않음 (무한 루프 방지)
    if (arraysEqual(currentInitialImages, prevInitialImages)) {
      return;
    }

    // 업로드 중인 이미지가 있으면 업데이트하지 않음 (사용자 작업 보호)
    setImages((prevImages) => {
      const hasUploading = prevImages.some((img) => img.uploading);
      if (hasUploading && !isInitialMountRef.current) {
        // 업로드 중이고 초기 마운트가 아니면 변경하지 않음
        return prevImages;
      }

      // 기존 이미지 URL 추출 (blob: 제외, 업로드 완료된 이미지만)
      const existingUrls = prevImages
        .filter((img) => !img.uploading && !img.error && !img.url.startsWith("blob:"))
        .map((img) => img.url);

      // initialImages와 기존 이미지 병합 (중복 제거)
      // initialImages에 있는 이미지는 우선적으로 유지 (서버에서 온 최신 데이터)
      const mergedUrls = Array.from(new Set([...currentInitialImages, ...existingUrls]));

      // maxImages 제한 적용
      const limitedUrls = mergedUrls.slice(0, maxImages);

      // UploadedImage 배열로 변환
      const loadedImages: UploadedImage[] = limitedUrls.map((url, index) => {
        // 기존 이미지인지 확인하여 ID 유지
        const existingImage = prevImages.find((img) => img.url === url);
        return {
          id: existingImage?.id || `img-${index}-${Date.now()}`,
          url: url,
          uploading: false,
        };
      });

      prevInitialImagesRef.current = currentInitialImages;

      // 제한을 초과한 경우 경고
      if (mergedUrls.length > maxImages) {
        console.warn(
          `[MarketImageUploader] Total images (${mergedUrls.length}) exceed maxImages (${maxImages}). Only showing first ${maxImages} images.`
        );
      }

      return loadedImages;
    });

    isInitialMountRef.current = false;
  }, [initialImages, controlledImages, maxImages]); // images를 의존성 배열에서 제거하여 무한 루프 방지

  // images state가 변경될 때마다 부모에게 알림
  useEffect(() => {
    const uploadedPaths = images
      .filter(
        (img) =>
          !img.uploading &&
          !img.error &&
          img.url &&
          typeof img.url === "string" &&
          !img.url.startsWith("blob:")
      )
      .map((img) => img.url);
    onImagesChange(uploadedPaths);
  }, [images, onImagesChange]);

  // 이미지 압축 옵션 (핸드폰 사진 최적화)
  // 핸드폰에서 찍은 사진(보통 3-10MB)을 자동으로 적절한 크기로 압축
  const compressionOptions = {
    maxSizeMB: 1, // 목표 크기: 1MB (웹 표시에 충분)
    maxWidthOrHeight: 1920, // 최대 해상도: 1920px (고해상도 디스플레이 지원)
    useWebWorker: true, // 백그라운드에서 압축하여 UI 블로킹 방지
    fileType: "image/jpeg" as const, // JPEG로 통일 (압축률 좋음)
    initialQuality: 0.85, // 초기 품질 85% (고품질 유지하면서 크기 최적화)
    alwaysKeepResolution: false, // 해상도 조정 허용
  };

  const uploadImage = async (file: File, imageId: string) => {
    try {
      // 1. Get R2 key from backend
      const uploadRes = await apiRequest("POST", "/api/market-images/upload", {
        fileName: file.name,
        fileType: file.type,
        fileSize: file.size,
      });

      // apiRequest는 throwIfResNotOk를 호출하므로 여기서는 ok 체크 불필요
      const uploadResponse = await uploadRes.json();
      // API 응답이 { ok: true, data: {...} } 형태일 수도 있고, 직접 데이터일 수도 있음
      const uploadData = uploadResponse.data || uploadResponse;

      if (!uploadData || !uploadData.key || !uploadData.uploadURL) {
        console.error(
          "[MarketImageUploader] Invalid upload response:",
          uploadResponse
        );
        throw new Error("이미지 업로드 키 요청 응답이 유효하지 않습니다");
      }

      console.log("[MarketImageUploader] Upload key received:", {
        key: uploadData.key,
        uploadURL: uploadData.uploadURL,
      });

      // 2. Upload file directly to commit endpoint
      const formData = new FormData();
      formData.append("file", file);
      formData.append("key", uploadData.key);

      const commitRes = await apiRequest(
        "POST",
        "/api/market-images/commit",
        formData
      );

      // apiRequest는 throwIfResNotOk를 호출하므로 여기서는 ok 체크 불필요
      const commitResponse = await commitRes.json();

      // API 응답이 { ok: true, data: {...} } 형태일 수도 있고, 직접 데이터일 수도 있음
      const commitData = commitResponse.data || commitResponse;

      // 4. Update local state
      // Use publicUrl (relative path) or url (full URL) for consistency
      // publicUrl이 상대 경로이므로 normalizeImageUrl로 정규화
      const rawUrl =
        commitData?.publicUrl || commitData?.objectPath || commitData?.url;

      if (!rawUrl) {
        console.error("[MarketImageUploader] No URL in commit response:", {
          commitResponse,
          commitData,
        });
        throw new Error("이미지 업로드 응답에 URL이 없습니다");
      }

      // rawUrl이 유효한지 확인
      if (!rawUrl || typeof rawUrl !== "string") {
        console.error("[MarketImageUploader] Invalid rawUrl:", rawUrl);
        throw new Error("이미지 업로드 응답에 유효한 URL이 없습니다");
      }

      const imageUrl = normalizeImageUrl(rawUrl) || rawUrl;

      // imageUrl도 유효한지 확인
      if (!imageUrl || typeof imageUrl !== "string") {
        console.error(
          "[MarketImageUploader] Invalid normalized imageUrl:",
          imageUrl
        );
        throw new Error("이미지 URL 정규화에 실패했습니다");
      }

      console.log("[MarketImageUploader] Image upload complete:", {
        rawUrl,
        normalizedUrl: imageUrl,
        commitData,
      });

      setImages((prev) =>
        prev.map((img) =>
          img.id === imageId ? { ...img, url: imageUrl, uploading: false } : img
        )
      );

      // useEffect에서 자동으로 onImagesChange 호출됨

      return commitData.objectPath;
    } catch (error) {
      console.error("Error uploading image:", error);
      setImages((prev) =>
        prev.map((img) =>
          img.id === imageId
            ? { ...img, uploading: false, error: "업로드 실패" }
            : img
        )
      );
      toast({
        title: "업로드 실패",
        description: "이미지 업로드 중 오류가 발생했습니다. 다시 시도해주세요.",
        variant: "destructive",
      });
      throw error;
    }
  };

  const handleFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;

    const remainingSlots = maxImages - images.length;
    if (remainingSlots <= 0) {
      toast({
        title: "최대 이미지 수 초과",
        description: `최대 ${maxImages}장까지 업로드 가능합니다.`,
        variant: "destructive",
      });
      return;
    }

    const filesToProcess = Array.from(files).slice(0, remainingSlots);

    // 여러 파일이 선택되었을 때 알림
    if (files.length > 1) {
      toast({
        title: `${files.length}장의 사진 선택됨`,
        description: `${filesToProcess.length}장을 업로드합니다.`,
      });
    }

    const newImages: UploadedImage[] = [];

    for (const file of filesToProcess) {
      // Check file type
      if (!file.type.startsWith("image/")) {
        toast({
          title: "잘못된 파일 형식",
          description: "이미지 파일만 업로드 가능합니다.",
          variant: "destructive",
        });
        continue;
      }

      // Check file size (before compression)
      // 핸드폰 사진은 보통 3-10MB이므로 충분히 큰 제한 설정
      if (file.size > 50 * 1024 * 1024) {
        // 50MB limit
        toast({
          title: "파일 크기 초과",
          description: "50MB 이하의 이미지만 업로드 가능합니다.",
          variant: "destructive",
        });
        continue;
      }

      try {
        // 이미지 압축 (핸드폰 사진 자동 최적화)
        // 원본이 작으면 압축하지 않고 그대로 사용
        let fileToUpload = file;

        // 원본이 1MB보다 크거나, 해상도가 1920px보다 큰 경우에만 압축
        if (file.size > 1024 * 1024) {
          try {
            const compressedFile = await imageCompression(
              file,
              compressionOptions
            );
            console.log(
              `[Image Upload] 압축 완료: ${(file.size / 1024 / 1024).toFixed(
                2
              )}MB → ${(compressedFile.size / 1024 / 1024).toFixed(2)}MB`
            );
            fileToUpload = compressedFile;
          } catch (compressionError) {
            console.warn(
              "[Image Upload] 압축 실패, 원본 파일 사용:",
              compressionError
            );
            // 압축 실패 시 원본 파일 사용 (서버에서 처리)
            fileToUpload = file;
          }
        } else {
          console.log(
            `[Image Upload] 작은 파일이므로 압축 생략: ${(
              file.size / 1024
            ).toFixed(2)}KB`
          );
        }

        const compressedFile = fileToUpload;
        const imageId = crypto.randomUUID();
        const localUrl = URL.createObjectURL(compressedFile);

        newImages.push({
          id: imageId,
          url: localUrl,
          file: compressedFile,
          uploading: true,
        });

        // Start upload in background
        uploadImage(compressedFile, imageId);
      } catch (error) {
        console.error("Error compressing image:", error);
        toast({
          title: "이미지 처리 실패",
          description: "이미지 압축 중 오류가 발생했습니다.",
          variant: "destructive",
        });
      }
    }

    setImages((prev) => [...prev, ...newImages]);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    handleFiles(e.dataTransfer.files);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const removeImage = (imageId: string) => {
    setImages((prev) => {
      const filtered = prev.filter((img) => img.id !== imageId);
      // useEffect에서 자동으로 onImagesChange 호출됨
      return filtered;
    });
  };

  const retryUpload = async (imageId: string) => {
    const image = images.find((img) => img.id === imageId);
    if (!image || !image.file) return;

    setImages((prev) =>
      prev.map((img) =>
        img.id === imageId ? { ...img, uploading: true, error: undefined } : img
      )
    );

    await uploadImage(image.file, imageId);
  };

  return (
    <div className="space-y-4">
      {/* Upload Area */}
      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
          isDragging
            ? "border-primary bg-primary/5"
            : "border-muted-foreground/25"
        } ${
          images.length >= maxImages ? "opacity-50 pointer-events-none" : ""
        }`}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={(e) => handleFiles(e.target.files)}
          className="hidden"
        />

        <div className="flex flex-col items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center">
            <Camera className="w-6 h-6 text-muted-foreground" />
          </div>

          <div>
            <p className="font-medium mb-1">
              사진을 추가해주세요 ({images.length}/{maxImages})
            </p>
            <p className="text-sm text-muted-foreground">
              드래그하거나 클릭하여 업로드 · 여러 장 선택 가능
            </p>
          </div>

          <Button
            type="button"
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            disabled={images.length >= maxImages}
            data-testid="button-select-images"
          >
            <ImageIcon className="w-4 h-4 mr-2" />
            이미지 선택
          </Button>

          <p className="text-xs text-muted-foreground">
            최대 {maxImages}장 · 여러 장 한 번에 선택 가능 · 각 2MB 이하 · JPG,
            PNG, WebP
          </p>
        </div>
      </div>

      {/* Image Preview Grid */}
      {images.length > 0 && (
        <div className="flex flex-wrap gap-3">
          {images.map((image, index) => (
            <div
              key={image.id}
              className="relative w-24 h-24 sm:w-28 sm:h-28 rounded-lg overflow-hidden bg-muted group"
              data-testid={`image-preview-${index}`}
            >
              {/* Image */}
              {image.url && (
                <img
                  src={
                    image.url.startsWith("blob:")
                      ? image.url
                      : normalizeImageUrl(image.url) || image.url
                  }
                  alt={`Upload ${index + 1}`}
                  className="w-full h-full object-cover"
                  loading="lazy"
                  onError={(e) => {
                    console.error("[MarketImageUploader] Image load error:", {
                      originalUrl: image.url,
                      normalizedUrl: normalizeImageUrl(image.url),
                      imageId: image.id,
                      isBlob: image.url.startsWith("blob:"),
                    });
                    // Fallback to placeholder
                    e.currentTarget.src =
                      'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="400"%3E%3Crect fill="%23e5e7eb" width="400" height="400"/%3E%3Ctext fill="%239ca3af" font-family="sans-serif" font-size="16" dy="200" dx="200" text-anchor="middle"%3EImage Error%3C/text%3E%3C/svg%3E';
                  }}
                />
              )}

              {/* Uploading Overlay */}
              {image.uploading && (
                <div className="absolute inset-0 bg-background/80 flex items-center justify-center">
                  <Loader2 className="w-6 h-6 animate-spin text-primary" />
                </div>
              )}

              {/* Error Overlay */}
              {image.error && (
                <div className="absolute inset-0 bg-destructive/80 flex flex-col items-center justify-center gap-2">
                  <p className="text-xs text-destructive-foreground px-2 text-center">
                    {image.error}
                  </p>
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    onClick={() => retryUpload(image.id)}
                    className="text-xs"
                  >
                    재시도
                  </Button>
                </div>
              )}

              {/* Index Badge (for first image = cover) */}
              {index === 0 && !image.uploading && !image.error && (
                <div className="absolute top-2 left-2 bg-primary text-primary-foreground text-xs px-2 py-1 rounded">
                  대표
                </div>
              )}

              {/* Delete Button */}
              <button
                type="button"
                onClick={() => removeImage(image.id)}
                className="absolute top-2 right-2 w-6 h-6 rounded-full bg-background/80 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                data-testid={`button-delete-image-${index}`}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Helper Text */}
      {images.length > 0 && (
        <p className="text-xs text-muted-foreground">
          💡 첫 번째 사진이 대표 이미지로 설정됩니다
        </p>
      )}
    </div>
  );
}
